<div class="page-head">
  <a class="back" routerLink="/orders/new">‹ All orders</a>
  @if (order(); as o) {
    <div class="title-row">
      <h1>{{ o.customerName || o.orderNumber || o.orderId }}</h1>
      <span class="status-pill">{{ statusLabel(o.status) }}</span>
    </div>
    <p class="muted">
      @if (o.orderNumber) {
        Order #{{ o.orderNumber }} ·
      }
      Placed {{ o.createdAt | date: "medium" }}
    </p>
  }
</div>

@if (loading()) {
  <p class="muted">Loading…</p>
} @else if (error(); as message) {
  <div class="error-banner">{{ message }}</div>
} @else if (order(); as o) {
  @if (message(); as note) {
    <div class="notice" [class.error]="messageError()">{{ note }}</div>
  }

  <section class="card progress-card">
    <header class="card-head"><h2>Progress</h2></header>
    <ol class="progress-stepper">
      @for (step of progress().steps; track step; let i = $index) {
        <li
          class="progress-step"
          [class.is-done]="progress().done[i]"
          [class.is-current]="i === progress().current"
        >
          <span class="progress-dot" aria-hidden="true"></span>
          <span class="progress-label">{{ step }}</span>
        </li>
      }
    </ol>
  </section>

  <div class="actions-grid">
    <section class="card">
      <header class="card-head"><h2>Order summary</h2></header>
      <ul class="summary-list">
        <li><span>Rooms</span><strong>{{ summary().rooms }}</strong></li>
        <li><span>Windows</span><strong>{{ summary().windows }}</strong></li>
        <li>
          <span>6-channel remotes</span><strong>{{ summary().remotes6 }}</strong>
        </li>
        <li>
          <span>16-channel remotes</span><strong>{{ summary().remotes16 }}</strong>
        </li>
        <li><span>Solar panels</span><strong>{{ summary().solar }}</strong></li>
      </ul>
    </section>

    <section class="card">
      <header class="card-head">
        <h2>Measurements</h2>
        @if (measurementsDone()) {
          <span class="done" title="Done">✓</span>
        }
      </header>
      <p class="status">
        Download a PDF sheet with every room, all window sizes, remote controls,
        the solar-panel count, and the customer's name and address — everything
        you need to build the order.
      </p>
      <button class="btn-primary" type="button" (click)="downloadMeasurements()">
        Download measurements (PDF)
      </button>
    </section>

    <section class="card">
      <header class="card-head">
        <h2>Manufacturing quote</h2>
        @if (o.quote) {
          <span class="done" title="Done">✓</span>
        }
      </header>
      <p class="status">
        Status: <strong>{{ statusLabel(o.quoteStatus) }}</strong>
        @if (o.quote) {
          · <a [href]="o.quote.url" target="_blank" rel="noopener">current file</a>
          @if (o.quote.amount != null) {
            · ${{ o.quote.amount }}
          }
        }
      </p>
      <p class="hint">
        Enter your manufacturing cost and attach your quote sheet (Excel or PDF).
      </p>
      @if (paid()) {
        <p class="hint locked">
          Locked — you've been paid, so the quote can no longer change.
        </p>
      }
      <div class="field">
        <label>Amount (USD)</label>
        <input
          type="number"
          [ngModel]="quoteAmount"
          (ngModelChange)="setQuoteAmount($event)"
          [disabled]="busy() || paid()"
          [class.invalid]="quoteAmountInvalid()"
        />
      </div>
      <div class="field">
        <label>Quote file <span class="req">required</span></label>
        <input
          type="file"
          accept=".xlsx,.xls,.pdf"
          (change)="onFileSelected($event)"
          [disabled]="busy() || paid()"
          [class.invalid]="quoteFileInvalid()"
        />
        @if (quoteFileName) {
          <span class="file-name">{{ quoteFileName }}</span>
        }
      </div>
      <button
        class="btn-primary"
        type="button"
        (click)="submitQuote()"
        [disabled]="busy() || paid()"
      >
        {{ busy() ? "Submitting…" : "Submit quote" }}
      </button>
    </section>

    <section class="card">
      <header class="card-head">
        <h2>Alibaba payment link</h2>
        @if (o.alibabaUrl) {
          <span class="done" title="Done">✓</span>
        }
      </header>
      @if (o.alibabaUrl) {
        <p class="status">
          Current:
          <a [href]="o.alibabaUrl" target="_blank" rel="noopener">open link</a>
        </p>
      }
      <p class="hint">
        Paste the Alibaba order/payment link so the shop can pay you.
      </p>
      <div class="field">
        <label>Alibaba link</label>
        <input
          type="url"
          [ngModel]="alibabaUrl"
          (ngModelChange)="setAlibaba($event)"
          placeholder="https://www.alibaba.com/…"
          [disabled]="busy() || paid()"
          [class.invalid]="alibabaInvalid()"
        />
      </div>
      <button
        class="btn-primary"
        type="button"
        (click)="saveAlibaba()"
        [disabled]="busy() || paid()"
      >
        Save link
      </button>
    </section>

    <section class="card">
      <header class="card-head">
        <h2>Shipping</h2>
        @if (o.trackingNumber) {
          <span class="done" title="Done">✓</span>
        }
      </header>
      <p class="hint">
        Add the SF Express waybill once shipped — the customer is emailed the
        tracking.
      </p>
      <div class="field">
        <label>SF Express waybill</label>
        <input
          type="text"
          [ngModel]="tracking"
          (ngModelChange)="setTracking($event)"
          placeholder="Tracking number"
          [disabled]="busy()"
          [class.invalid]="trackingInvalid()"
        />
      </div>
      <button class="btn-primary" type="button" (click)="setShipping()" [disabled]="busy()">
        Save tracking
      </button>
    </section>
  </div>

  <section class="card">
    <header class="card-head">
      <h2>Windows &amp; accessories</h2>
      <span class="count">{{ o.items.length }}</span>
    </header>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Window</th>
            <th>Style</th>
            <th>Control</th>
            <th>Solar</th>
            <th>Shading</th>
            <th class="num">Width</th>
            <th class="num">Height</th>
          </tr>
        </thead>
        <tbody>
          @for (grp of roomGroups(o); track grp.room) {
            <tr class="room-row">
              <td colspan="7">{{ grp.room }}</td>
            </tr>
            @for (it of grp.items; track $index) {
              <tr>
                <td>{{ it.isRemote ? "Remote control" : it.label || "—" }}</td>
                <td>{{ it.blindCode }}</td>
                <td>{{ it.isRemote ? "—" : controlLabel(it.controlType) }}</td>
                <td>{{ it.hasSolarPanel ? "Yes" : "—" }}</td>
                <td>{{ it.isRemote ? "—" : it.opacity || "—" }}</td>
                <td class="num">{{ it.isRemote || it.width == null ? "—" : it.width + (it.unit === "cm" ? " cm" : '"') }}</td>
                <td class="num">{{ it.isRemote || it.height == null ? "—" : it.height + (it.unit === "cm" ? " cm" : '"') }}</td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  </section>

  @if (o.shippingAddress; as addr) {
    <section class="card">
      <header class="card-head"><h2>Ship to</h2></header>
      <p class="address">
        @if (addr.name) {
          <strong>{{ addr.name }}</strong><br />
        }
        @if (addr.line1) {
          {{ addr.line1 }}{{ addr.line2 ? ", " + addr.line2 : "" }}<br />
        }
        @if (addr.city || addr.state || addr.zip) {
          {{ addr.city }}, {{ addr.state }} {{ addr.zip }}<br />
        }
        @if (addr.phone) {
          {{ addr.phone }}
        }
        @if (!addr.line1 && !addr.city && !addr.state && !addr.zip) {
          <span class="muted">No address on file.</span>
        }
      </p>
    </section>
  }
}
